/* The till's layout glue. Tokens only, same as the app.
 *
 * Everything here is sized for a tablet on a stand, read at arm's length,
 * while looking at a person rather than at the screen. That is the whole
 * budget: two seconds of glance.
 *
 * Values marked as artboard values were read off the live A1 artboard with
 * getComputedStyle rather than eyeballed from a screenshot, because "looks
 * about right" is how a build ends up a whole direction away from the design.
 *
 * Green ticks and red crosses are absent on purpose: "no refusal implies the
 * customer did anything wrong", and a big alarm-coloured state says otherwise
 * no matter what the words underneath it are.
 */
export const counterCss = `
.till {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  /* Deliberately transparent. The shell gradient is painted on body; repeating
     --paper here covers it with a flat plum and the ground loses its depth,
     which was most of the difference from the artboard. */
  background: transparent;

  /* SIZED FOR THE TABLET IT IS BUILT FOR (review of 2026-09-18, findings 5 and
     8). The till was drawn for a phone: at 820 the keypad was 298px wide in
     the middle of an empty screen, and the digits being typed were small at
     arm's length. Everything here grows with the screen's SHORT side, so a
     tablet in portrait or landscape gets the same pad, and a phone gets
     exactly what it had (every clamp's floor is the old value).

     At 820 x 1180: a 525px column, 82px keys with 28px digits, 90px code
     cells, 69px outcome buttons. It still fits a 1280 x 720 laptop window
     without scrolling. */
  --till-col: clamp(380px, 64vmin, 580px);
  --till-col-wide: clamp(480px, 72vmin, 660px);
  --counter-btn-h: clamp(56px, 8.4vmin, 72px);
  --counter-btn-fs: clamp(16px, 2.3vmin, 20px);
}

.till-bar {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-5);
  padding-top: calc(var(--space-3) + env(safe-area-inset-top));
  border-bottom: 1px solid var(--border-soft);
}
/* The header's one button -- "End shift" on the kiosk, "Back to the dashboard"
   inside it -- is 44px to press (review of 2026-09-18, finding 9). */
.till-bar .linkish {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}
/* Under 420px the mark alone, which keeps its name for a screen reader: at
   360, beside "Back to the dashboard", the word "Damga" cost the shop's own
   name ("Fixture Ro...", review finding 10). The shop's name is the one a
   cashier needs to see. */
@media (max-width: 420px) {
  .till-brand > span > span { display: none; }
}
.till-where {
  flex: 1;
  min-width: 0;
  color: var(--ink-faint);
  font-size: var(--text-small);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.till-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
}

.muted { margin: 0; color: var(--ink-soft); }
.till-error { margin: 0; color: var(--danger); font-size: var(--text-small); }
.screen-title { margin: 0; font-size: var(--text-title); font-weight: 600; letter-spacing: -0.015em; }
.linkish {
  border: 0; background: none; padding: var(--space-2); margin: calc(var(--space-2) * -1);
  color: var(--ink-faint); font: inherit; font-size: var(--text-small); cursor: pointer;
}

/* --- the keypad ---------------------------------------------------------- */
.pad {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5);
  width: 100%;
  max-width: var(--till-col, 380px);
  /* The keys are the till's biggest targets: hit hundreds of times a day. */
  --counter-btn-h: clamp(56px, 10vmin, 92px);
  --counter-btn-fs: clamp(16px, 3.4vmin, 28px);
}

/* Six fixed slots. The field does not change width as it fills, so the eye
   does not have to find it again between digits. */
.pad-code {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-2);
  width: 100%;
  margin: 0;
}
.pad-code > span {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: clamp(64px, 11vmin, 100px);
  border: 1px solid var(--border);
  border-radius: var(--radius-chip);
  background: var(--panel-inset, var(--paper-card));
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: clamp(30px, 5.4vmin, 48px);
  font-weight: 600;
  color: var(--ink);
}
.pad-code > span[data-filled] { border-color: var(--ink); }

.pad-keys {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(12px, 2vmin, 18px);
  width: 100%;
}
.pad-busy { font-size: var(--text-small); }

/* --- the outcome --------------------------------------------------------- */
.outcome {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  width: 100%;
  max-width: var(--till-col-wide, 480px);
}

.stamped { display: flex; flex-direction: column; gap: var(--space-4); align-items: stretch; }

/* Darker than the ground, not lighter: the artboard's member panel is
   translucent near-black over the plum, so it reads as a recess. */
.stamped-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: 20px 22px;
  background: var(--panel-inset, var(--paper-card));
  border: 1px solid var(--border);
  border-radius: var(--panel-inset-radius, var(--radius-card));
}
.stamped-row { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-3); }
/* A name and a masked number side by side both wrapped at 360 and 390 (review
   finding 7): under 420px the number goes under the name. */
@media (max-width: 420px) {
  .stamped-row { flex-direction: column; align-items: flex-start; gap: 2px; }
}
/* The card's marks grow with the till; StampGrid's own size is the floor. */
.stamped-card { --till-pip: clamp(26px, 4.4vmin, 40px); }
.stamped-card .dg-pips { grid-template-columns: repeat(var(--pip-cols, 8), var(--till-pip)); }
.stamped-card .dg-pips > span { width: var(--till-pip); height: var(--till-pip); }
.stamped-foot { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; }

/* The undo reads as a mono label because that is what the artboard shows, but
   it is a real control: the three minute window is only reachable if the way
   out is one tap from the moment of success. */
/* A 44px TARGET WITH AN EDGE (2026-09-18). The UI review measured it at 30px
   tall with nothing around it, beside a large button: on a tablet it read as a
   caption, not as the way out of a wrong stamp. Same mono words, now a control. */
.undo {
  /* SEEN, NOT JUST SIZED (2026-09-18). The review made this a 44px outlined
     control; in the end-to-end test, on staging's real ground, the outline all
     but vanished, because --line-strong is defined faint here and the fallback
     never applied. Explicit, and about 4:1 against the till's ground: a
     control's edge needs 3:1 to read as a control (WCAG 1.4.11). */
  border: 1px solid rgba(240, 251, 250, 0.45);
  border-radius: 999px;
  background: none;
  /* It grows with "Next customer" beside it, or on a tablet it goes back to
     reading as a caption, which is the finding this control came from. */
  min-height: max(44px, calc(var(--counter-btn-h, 56px) - 12px));
  padding: 0 clamp(16px, 2.6vmin, 22px);
  color: var(--ink-faint);
  font-family: var(--font-mono);
  font-size: clamp(11px, 1.6vmin, 14px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  cursor: pointer;
}
.undo:active { color: var(--seal); }
.stamped-tail { margin: 0; color: var(--ink-soft); font-size: var(--text-small); }
/* 26px/600 at -0.015em, which is what the A1 artboard computes. The base
   system's --text-display is larger and belongs to a different direction. */
.stamped-title {
  margin: 0;
  font-size: clamp(26px, 4.2vmin, 38px);
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.2;
}
.stamped-who { margin: 0; font-size: clamp(18px, 2.8vmin, 24px); font-weight: 600; }
.stamped-expired { font-size: var(--text-small); }

.till-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  /* What went wrong and what to say, read at arm's length (finding 8). */
  font-size: clamp(16px, 2.3vmin, 19px);
  padding: 20px 22px;
  background: var(--panel-inset, var(--paper-card));
  border: 1px solid var(--border);
  border-radius: var(--panel-inset-radius, var(--radius-card));
}
/* A refusal that needs attention gets a thin rule, never a fill. "No refusal
   implies the customer did anything wrong", and a big alarm-coloured panel says
   otherwise whatever the words are. In the teal register the rule is --danger,
   as the artboard draws A6; in plum it had to be the accent. */
.till-panel[data-tone="bad"] { border-inline-start: 2px solid var(--danger); }
/* A held stamp is not a refusal and not a success, and the panel must not
   borrow either register — the grey "Saved" panel was exactly that mistake in
   the other direction.

   WARNING, AND DASHED. In the plum register --warning and --seal were within a
   few points of each other, so this had to be the accent told apart by a dash.
   In teal they are far apart, so it takes --warning, as the artboard draws A4,
   and keeps the dash: unfinished reads as unfinished.

   This is the quiet baseline. The escalating treatment as the six hours run
   down is a separate decision and is with the founder. */
.till-panel[data-tone="holding"] { border-inline-start: 2px dashed var(--warning); }
/* Stage three and four. The dash goes solid: the same warning, no longer
   unfinished. */
.till-panel[data-tone="holding-urgent"] { border-inline-start: 2px solid var(--warning); }

/* --- the header, across the six hours -------------------------------------
   Stage three: the count stops being incidental and takes the accent.
   Stage four: a band rather than a line. The ONE structural change in the
   whole sequence, and it happens once. Still not a dialogue — ruling 02, never
   interrupt: the till keeps taking stamps, because a counter that stops
   working to complain about its queue costs the merchant more than the queue
   does, and the shop being busy is usually why nobody noticed the connection.

   The first three hours change nothing at all. That is deliberate and it is
   not a control asleep: the ninety-minute till-silence email has already
   fired. The alert is the control early and this bar is the control late, one
   mechanism seen from two ends. Do not trim either as duplication. */
.till-bar[data-stage="pressing"] .till-where { color: var(--warning); font-weight: 600; }
.till-bar[data-stage="last"] {
  background: var(--warning-bg);
  border-bottom-color: var(--warning);
}
.till-bar[data-stage="last"] .till-where { color: var(--warning); font-weight: 600; }

/* The merchant's line, under the customer's. Set apart the way "Say this" is,
   because it is addressed to somebody else standing on this side of the
   screen. Quieter than the body rather than louder: it is the sentence that
   makes a person go and look at the router, and it does not need shouting to
   do that. */
.merchant-note {
  margin: 0;
  padding-top: var(--space-2, 8px);
  border-top: 1px solid var(--border-soft);
  font-size: 0.9em;
  color: var(--seal-text);
}
.till-panel-title {
  margin: 0;
  font-size: var(--text-title);
  font-weight: 600;
  letter-spacing: -0.015em;
}

/* "Say this" is a script for the person at the till, so it is set apart from
   the explanation rather than reading as more explanation. */
.say {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-4);
  background: var(--paper-card);
  border-radius: var(--radius-chip);
  color: var(--ink);
}

/* --- staff sign-in ------------------------------------------------------- */
.till-signin {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: 380px;
  margin: auto;
  padding: var(--space-6);
}

.input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
}
.input-group:focus-within { border-color: var(--seal); }
.input-prefix {
  display: flex; align-items: center; padding: 0 var(--space-3);
  border-inline-end: 1px solid var(--border); color: var(--ink-faint);
}
.till-input {
  flex: 1; min-width: 0; min-height: 56px; padding: var(--space-3);
  border: 0; background: transparent; color: var(--ink);
  /* 16px minimum, or iOS zooms on focus and does not zoom back. */
  font-size: 18px; font-family: var(--font-mono);
}
.till-input:focus { outline: none; }
.input--code {
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
  font-size: 30px;
  letter-spacing: 0.3em;
  text-align: center;
}

/* --- finding a customer without a code ------------------------------------
 *
 * Same column width as the keypad, deliberately: this is the other half of one
 * screen, not a different place, and a cashier who has just failed to stamp
 * somebody should not also have to re-find the layout.
 *
 * Every hit is one tall row with the name on the left and the card on the
 * right. Tall because it is a tablet on a counter being tapped by somebody
 * holding a cup, and because a mis-tap here stamps the wrong person -- which
 * is the exact failure the masked-candidate picker used to produce and this
 * screen replaced.
 */
.lookup {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: var(--till-col, 380px);
}
.lookup-why {
  display: flex; flex-direction: column; gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel, 18px);
  background: var(--panel-inset, var(--paper-card));
}
.lookup-form { display: flex; gap: var(--space-3); align-items: stretch; }
.lookup-form .till-input {
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
  font-family: var(--font-sans);
  font-size: 18px;
}
.lookup-form .till-input:focus { outline: none; border-color: var(--seal); }
.lookup-hint, .lookup-scope { margin: 0; font-size: 13px; }
.lookup-results { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--space-2); }
.lookup-hit {
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
  width: 100%; min-height: 64px; padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
  color: var(--ink);
  font: inherit;
  text-align: start;
  cursor: pointer;
}
.lookup-hit:hover:not(:disabled) { border-color: var(--seal); }
.lookup-hit:focus-visible { outline: 2px solid var(--seal); outline-offset: 2px; }
.lookup-hit:disabled { opacity: 0.5; cursor: default; }
.lookup-who { font-size: 17px; }
.lookup-card { color: var(--ink-faint); font-size: 15px; }

/* till-main centres one child. The keypad and its escape hatch are one
   column, or the link lands beside the keys instead of under them -- which is
   what it did on the first render. */
.till-pad {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Full width, so the pad's own max-width decides its size. Without this the
     column shrank to its contents and the pad was 298px at 820. */
  width: 100%;
}

/* Under the keypad, quiet. It is the way out of a situation that has not
   happened yet, so it must not compete with the six digits. */
.till-find {
  margin-top: var(--space-5);
  align-self: center;
  font-size: clamp(14px, 2vmin, 17px);
  /* 44px to press (review of 2026-09-18, finding 9): these are the ways out of
     a situation, and a way out is pressed in a hurry. */
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}
/* "Give a reward" sits close under "No code? Find by phone": two quiet doors,
   one group. */
.till-find + .till-find {
  margin-top: 0;
}

/* --- enrolling somebody at the counter ------------------------------------
 *
 * The consent line is set at the same size as the rest of the form rather than
 * shrunk to a footnote. It is the sentence a cashier has to say out loud and
 * the sentence a regulator would ask about, and a control nobody can read at
 * arm's length on a propped-up tablet is a control in name.
 */
.enrol-form { display: flex; flex-direction: column; gap: var(--space-3); width: 100%; }
.enrol-form .till-input {
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
  font-family: var(--font-sans);
  font-size: 18px;
}
.enrol-form .till-input:focus { outline: none; border-color: var(--seal); }
.enrol-consent {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: var(--panel-inset, var(--paper-card));
  cursor: pointer;
}
.enrol-consent input {
  /* 24px. Tapped by somebody holding a cup, and a mis-tap here is a consent
     record that says yes when nobody said yes. */
  inline-size: 24px;
  block-size: 24px;
  margin-block-start: 1px;
  flex: none;
  accent-color: var(--seal);
}
.enrol-consent > span { display: flex; flex-direction: column; gap: var(--space-2); }
.enrol-said { color: var(--ink-soft); font-size: 15px; line-height: 1.5; }

/* THE READ-BACK (047). The address is the only thing on the screen, split at
   the @ because the halves are read aloud differently, in the mono face so an
   l and a 1 and an I are three different shapes, and large enough to be read
   letter by letter across a counter. overflow-wrap: anywhere, because an
   address has no spaces and a long one must wrap rather than run off a tablet. */
.readback {
  margin: 8px 0 4px;
  display: grid;
  gap: 4px;
  font-family: var(--font-mono);
  /* Sized so a typical address sits on one line in the till's column; a long
     one wraps at the <wbr> points Enrol.tsx inserts, and only an address with
     none of them falls back to breaking anywhere. */
  font-size: clamp(22px, 3.2vw, 30px);
  line-height: 1.2;
  letter-spacing: 0.02em;
  word-break: keep-all;
  overflow-wrap: anywhere;
  color: var(--ink);
}
.readback-domain { color: var(--ink-soft); }

/* What happened to the address, under the stamp. Kept reads as a quiet
   confirmation; not kept reads as information, never as an error, because the
   stamp landed either way. */
.stamped-email { margin: 0; font-size: 15px; line-height: 1.5; color: var(--ink); }
`;
